import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { supabase } from "../../lib/supabase";
import AuthService from "../../services/authService";
import MfaSetupModal from "../../views/login/components/mfaSetupModal";
import { MFA_UPDATED_EVENT } from "../../components/MfaPendingBanner";

const SecurityCard = ({ onSnackbar }) => {
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [factorId, setFactorId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [setupModal, setSetupModal] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [pendingFactorId, setPendingFactorId] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const loadMfaStatus = async () => {
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = data?.totp?.find((f) => f.status === "verified");
      if (verified) {
        setMfaEnabled(true);
        setFactorId(verified.id);
      } else {
        setMfaEnabled(false);
        setFactorId(null);
      }
    } catch {
      setMfaEnabled(false);
    }
  };

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const handleChangeAuthenticator = async () => {
    setLoading(true);
    try {
      if (factorId) {
        await AuthService.unenrollMfa(factorId);
      }
      const factor = await AuthService.enrollMfa();
      setQrUrl(factor.totp.uri);
      setSecret(factor.totp.secret);
      setPendingFactorId(factor.id);
      setSetupModal(true);
    } catch {
      onSnackbar("error", "No se pudo iniciar el cambio de autenticador.");
      await loadMfaStatus();
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setLoading(true);
    try {
      const factor = await AuthService.enrollMfa();
      setQrUrl(factor.totp.uri);
      setSecret(factor.totp.secret);
      setPendingFactorId(factor.id);
      setSetupModal(true);
    } catch {
      onSnackbar("error", "No se pudo iniciar la configuración de 2FA.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code) => {
    setVerifyLoading(true);
    try {
      const { data: challenge, error: challengeErr } =
        await supabase.auth.mfa.challenge({ factorId: pendingFactorId });
      if (challengeErr) throw challengeErr;

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: pendingFactorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyErr) throw verifyErr;

      setSetupModal(false);
      await loadMfaStatus();
      window.dispatchEvent(new Event(MFA_UPDATED_EVENT));
      onSnackbar("success", mfaEnabled
        ? "Autenticador cambiado correctamente."
        : "Autenticación en dos pasos activada.");
      return true;
    } catch {
      setVerifyLoading(false);
      return false;
    }
  };

  const handleCloseModal = () => {
    setSetupModal(false);
    if (pendingFactorId) {
      supabase.auth.mfa.unenroll({ factorId: pendingFactorId }).catch(() => {});
      setPendingFactorId(null);
      loadMfaStatus();
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "grey.50",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShieldOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              Seguridad
            </Typography>
          </Box>
          {mfaEnabled !== null && (
            <Chip
              icon={
                mfaEnabled ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                )
              }
              label={mfaEnabled ? "2FA activado" : "2FA pendiente"}
              size="small"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                height: 24,
                backgroundColor: mfaEnabled ? "#dcfce7" : "#fef3c7",
                color: mfaEnabled ? "#166534" : "#92400e",
                "& .MuiChip-icon": {
                  color: mfaEnabled ? "#16a34a" : "#d97706",
                },
              }}
            />
          )}
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La autenticación en dos pasos (2FA) es obligatoria. Al iniciar
            sesión, además de tu contraseña, necesitás ingresar un código de tu
            app de autenticación (Google Authenticator, Authy, etc.). El código
            se pide cada 12 horas.
          </Typography>

          {mfaEnabled === null ? (
            <CircularProgress size={20} />
          ) : mfaEnabled ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleChangeAuthenticator}
              disabled={loading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Cambiar autenticador"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleEnroll}
              disabled={loading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "0.8rem",
                backgroundColor: "#2563EB",
                "&:hover": { backgroundColor: "#1D4ED8" },
              }}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Activar 2FA"
              )}
            </Button>
          )}
        </Box>
      </Paper>

      <MfaSetupModal
        open={setupModal}
        onClose={handleCloseModal}
        qrUrl={qrUrl}
        secret={secret}
        onVerify={handleVerify}
        loading={verifyLoading}
        step={1}
      />
    </>
  );
};

export default SecurityCard;
